import {
    type FC
} from "react";
import {
    type GestureResponderEvent,
    Pressable,
    Platform,
    View
} from "react-native";
import type IQuestionnaireChoiceProps from "./type";
import stylesheet, {
    getQuestionnaireChoiceColors,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    usePointerType
} from "../../../../helpers/pointer";
import type {
    QuestionnaireItemPartContentProps,
    QuestionnaireKeyboardEventType
} from "../../type";
import {
    getQuestionnaireIsPlainEnterEvent,
    getQuestionnaireSelectBehaviour,
    getQuestionnaireSelectedValues,
    getQuestionnaireChoiceShortcut,
    getQuestionnaireIsEnterEvent,
    QUESTIONNAIRE_ARROW_STEPS,
    QUESTIONNAIRE_TOGGLE_KEY,
    QUESTIONNAIRE_ENTER_KEY
} from "../../util";
import {
    getHorizontalStep
} from "../../../../helpers/direction";
import {
    withQuestionnaireItemContext
} from "../../context";
import {
    getQuestionnaireMeasures
} from "../../stylesheet";
import RadioButton from "../../../radioButton";
import StateLayer from "../../../stateLayer";
import CheckBox from "../../../checkBox";
import Text from "../../../text";
import Kbd from "../../../kbd";

const QuestionnaireChoiceContent: FC<QuestionnaireItemPartContentProps<IQuestionnaireChoiceProps>> = ({
    customTheme: customThemeProp,
    itemContext,
    onHoverOut,
    onPressOut,
    onHoverIn,
    onPressIn,
    context,
    onFocus,
    choice,
    onBlur,
    index,
    style,
    ...props
}) => {
    const customTheme = customThemeProp ?? context.customTheme;

    const {
        controlHeights,
        shapeConfigs,
        touchTargets,
        typography,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    const pointerType = usePointerType();

    const {
        item
    } = itemContext;

    const selectBehaviour = getQuestionnaireSelectBehaviour(item);

    const isMultiple = selectBehaviour === "multiple";

    const selectedValues = getQuestionnaireSelectedValues({
        answer: itemContext.answer,
        item
    });

    const isChecked = selectedValues.includes(choice.value);

    const isDisabled = !!choice.isDisabled || context.isDisabled;

    const isWeb = Platform.OS === "web";

    const isShortcutVisible = isWeb && pointerType === "fine";

    const firstEnabledIndex = (item.choices ?? []).findIndex((currentChoice) => !currentChoice.isDisabled);

    const isTabStop = isMultiple || isChecked || selectedValues.length === 0 && index === firstEnabledIndex;

    const shortcut = getQuestionnaireChoiceShortcut({
        choice,
        index
    });

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        isDisabled,
        onHoverOut,
        onPressOut,
        onHoverIn,
        onPressIn,
        onFocus,
        onBlur
    });

    const currentMeasures = getQuestionnaireMeasures({
        size: context.size,
        controlHeights,
        touchTargets,
        pointerType,
        typography
    });

    const currentColors = getQuestionnaireChoiceColors({
        isDisabled,
        colors
    });

    const {
        container: containerDynamicStyle,
        indicator: indicatorDynamicStyle,
        shortcut: shortcutDynamicStyle
    } = useStyles({
        isError: itemContext.isInvalid,
        currentMeasures,
        isFocusVisible,
        shapeConfigs,
        isDisabled,
        iconSizes,
        isChecked,
        radiuses,
        borders,
        colors,
        spaces
    });

    const handleSelect = () => {
        if(isDisabled || itemContext.isLocked) {
            return;
        }

        context.handleChoiceChange({
            value: choice.value,
            item
        });
    };

    const onPress = (event: GestureResponderEvent) => {
        if(getQuestionnaireIsEnterEvent(event)) {
            return;
        }

        handleSelect();
    };

    const onKeyDown = (event: QuestionnaireKeyboardEventType) => {
        if(event.key === QUESTIONNAIRE_ENTER_KEY) {
            if(!getQuestionnaireIsPlainEnterEvent(event)) {
                return;
            }

            event.preventDefault();

            context.handleNext();

            return;
        }

        if(event.key === QUESTIONNAIRE_TOGGLE_KEY) {
            event.preventDefault();

            return;
        }

        const step = QUESTIONNAIRE_ARROW_STEPS[event.key] ?? getHorizontalStep({
            key: event.key,
            direction
        });

        if(!step || isMultiple || itemContext.isLocked) {
            return;
        }

        event.preventDefault();

        context.handleChoiceMove({
            index,
            item,
            step
        });
    };

    const onKeyUp = (event: QuestionnaireKeyboardEventType) => {
        if(event.key !== QUESTIONNAIRE_TOGGLE_KEY) {
            return;
        }

        event.preventDefault();

        handleSelect();
    };

    const labelId = `${itemContext.titleId}-choice-${index}`;

    const descriptionId = `${labelId}-description`;

    const tabIndex: 0 | -1 = isTabStop ? 0 : -1;

    const accessibilityProps = isWeb ? {
        "aria-describedby": choice.description ? descriptionId : undefined,
        "aria-invalid": itemContext.isInvalid ? true : undefined,
        "aria-keyshortcuts": shortcut,
        "aria-labelledby": labelId,
        onKeyDown,
        tabIndex,
        onKeyUp
    } : {
        accessibilityHint: choice.description,
        accessibilityLabel: choice.title
    };

    const renderIndicator = () => {
        const indicatorProps = {
            accessibilityLabel: choice.title,
            spreadBehaviour: "free" as const,
            isError: itemContext.isInvalid,
            customTheme,
            isDisabled,
            isChecked
        };

        return <View
            importantForAccessibility="no-hide-descendants"
            accessibilityElementsHidden={true}
            style={[
                stylesheet.indicator,
                indicatorDynamicStyle
            ]}
            aria-hidden={true}
        >
            {
                isMultiple ?
                    <CheckBox
                        {...indicatorProps}
                    />
                :
                    <RadioButton
                        {...indicatorProps}
                    />
            }
        </View>;
    };

    const renderShortcut = () => {
        if(!isShortcutVisible) {
            return null;
        }

        return <View
            style={[
                stylesheet.shortcut,
                shortcutDynamicStyle
            ]}
        >
            <Kbd
                customLocalize={context.customLocalize}
                customTheme={customTheme}
                isDisabled={isDisabled}
                aria-hidden={true}
                keys={shortcut}
                size="small"
            />
        </View>;
    };

    return <Pressable
        {...props}
        {...interactionHandlers}
        {...accessibilityProps}
        ref={(node) => {
            context.registerChoice(index, node);
        }}
        role={isMultiple ? "checkbox" : "radio"}
        aria-disabled={isDisabled}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        aria-checked={isChecked}
        disabled={isDisabled}
        onPress={onPress}
    >
        <StateLayer
            isHovered={!isDisabled && isHovered}
            isPressed={!isDisabled && isPressed}
            borderRadius={radiuses.actions}
            borderWidth={borders.line}
            customTheme={customTheme}
            isSquircle={true}
        />
        {renderIndicator()}
        <View
            style={stylesheet.label}
        >
            <Text
                variant={currentMeasures.labelVariant}
                customColor={currentColors.titleColor}
                id={isWeb ? labelId : undefined}
                customTheme={customTheme}
            >
                {choice.title}
            </Text>
            {
                choice.description ?
                    <Text
                        customColor={currentColors.subTitleColor}
                        id={isWeb ? descriptionId : undefined}
                        customTheme={customTheme}
                        variant="bodyMediumSize"
                    >
                        {choice.description}
                    </Text>
                :
                    null
            }
        </View>
        {renderShortcut()}
    </Pressable>;
};
export default withQuestionnaireItemContext("Choice", QuestionnaireChoiceContent);
